export type InsightConfigLike = {
	id?: string;
	title?: string;
	description?: string;
	type?: string;
	attributionPreset?: string;
	series?: Array<{
		id?: string;
		label?: string;
		metric?: string;
		filters?: Array<{
			id?: string;
			name?: string;
			operator?: string;
			value?: string | number | (string | number)[];
		}>;
		color?: string;
	}>;
	filters?: Array<{
		id?: string;
		name?: string;
		operator?: string;
		value?: string | number | (string | number)[];
	}>;
	filterOperator?: "and" | "or" | string;
	breakdown?: Array<{
		dimension?: string;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc" | string;
	}>;
	timeRange?: {
		preset?: string;
		customDays?: number;
	};
	useOwnTimeRange?: boolean;
	granularity?: "none" | "daily" | "weekly" | "monthly" | string;
	comparisonPeriod?: string;
	displayOptions?: {
		showLegend?: boolean;
		showMultipleYAxes?: boolean;
		showSummaryStats?: boolean;
		yAxisUnit?: {
			type?: string;
			customValue?: string;
		};
	};
	schemaVersion?: number;
	createdAt?: number;
	updatedAt?: number;
};

export type CustomInsightsApiFilter = {
	id?: string;
	name: string;
	operator: string;
	value: string | number | (string | number)[];
};

export type CustomInsightsApiSeries = {
	id?: string;
	metric: string;
	filters?: CustomInsightsApiFilter[];
	label?: string;
	color?: string;
	series_order?: number;
};

export type CustomInsightsApiBreakdown = {
	id?: string;
	dimension: string;
	sort_order?: "asc" | "desc";
	sort_by?: string | string[];
	limit?: number;
	breakdown_order?: number;
};

export type CustomInsightsApiInsight = {
	id: string;
	organisation?: string;
	title: string;
	description?: string;
	type: string;
	attribution_preset_id?: string;
	series: CustomInsightsApiSeries[];
	filters?: CustomInsightsApiFilter[];
	filter_operator?: "and" | "or";
	breakdowns?: CustomInsightsApiBreakdown[];
	granularity?: "none" | "daily" | "weekly" | "monthly";
	comparison_period?: string;
	display_options?: {
		show_legend?: boolean;
		show_multiple_y_axes?: boolean;
		show_summary_stats?: boolean;
		y_axis_unit?: {
			type?: string;
			custom_value?: string;
		};
	};
	time_range?: {
		preset?: string;
		custom_days?: number;
	};
	use_own_time_range?: boolean;
	created_by?: string;
	updated_by?: string;
	created_at?: string;
	updated_at?: string;
};

export type CustomInsightsApiListResponse = {
	items: CustomInsightsApiInsight[];
	count: number;
};

export type CustomInsightsApiUpsertInput = {
	type?: string;
	attribution_preset_id?: string;
	series?: Array<{
		metric: string;
		filters?: Array<{
			name: string;
			operator: string;
			value: string | number | (string | number)[];
		}>;
		label?: string;
		color?: string;
		series_order?: number;
	}>;
	filters?: Array<{
		name: string;
		operator: string;
		value: string | number | (string | number)[];
	}>;
	filter_operator?: "and" | "or";
	breakdowns?: Array<{
		dimension: string;
		sort_order?: "asc" | "desc";
		sort_by?: string[];
		limit?: number;
		breakdown_order?: number;
	}>;
	time_range?: {
		preset?: string;
		custom_days?: number;
	};
	granularity?: "none" | "daily" | "weekly" | "monthly";
	comparison_period?: string;
	display_options?: {
		show_legend?: boolean;
		show_multiple_y_axes?: boolean;
		show_summary_stats?: boolean;
		y_axis_unit?: {
			type?: string;
			custom_value?: string;
		};
	};
	title?: string;
	description?: string;
	use_own_time_range?: boolean;
};

export type CustomInsightsApiDashboardLayout = {
	x: number;
	y: number;
	w: number;
	h: number;
	min_w?: number;
	max_w?: number;
	min_h?: number;
	max_h?: number;
};

export type CustomInsightsApiDashboardTile = {
	id?: string;
	insight_id: string;
	layouts: Record<string, CustomInsightsApiDashboardLayout>;
	tile_order?: number;
};

export type CustomInsightsApiDashboard = {
	id: string;
	organisation?: string;
	title: string;
	description?: string;
	tiles: CustomInsightsApiDashboardTile[];
	default_time_range?: {
		preset?: string;
		custom_days?: number;
	} | null;
	default_attribution_preset_id?: string | null;
	schema_version?: number;
	created_by?: string;
	updated_by?: string;
	created_at?: string;
	updated_at?: string;
};

export type CustomInsightsApiDashboardListResponse = {
	items: CustomInsightsApiDashboard[];
	count: number;
};

export type CustomInsightsApiDashboardUpsertInput = {
	title?: string;
	description?: string;
	tiles?: Array<{
		insight_id: string;
		layouts: Record<string, CustomInsightsApiDashboardLayout>;
		tile_order?: number;
	}>;
	default_time_range?: {
		preset?: string;
		custom_days?: number;
	} | null;
	default_attribution_preset_id?: string | null;
};

export type CustomInsightsApiRequestOptions = {
	baseUrl: string;
	organisationId: string;
	token?: string | null;
	fetchImpl?: typeof fetch;
	signal?: AbortSignal;
};

export class CustomInsightsApiRequestError extends Error {
	readonly status: number;
	readonly body: unknown;

	constructor(params: { message: string; status: number; body: unknown }) {
		super(params.message);
		this.name = "CustomInsightsApiRequestError";
		this.status = params.status;
		this.body = params.body;
	}
}

const DEFAULT_INSIGHT_TYPE = "line";
const DEFAULT_METRIC = "customer_revenue";
const DEFAULT_GRANULARITY = "daily";
const DEFAULT_COMPARISON_PERIOD = "no_comparison";
const DEFAULT_TIME_RANGE_PRESET = "last_7_days";

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toTrimmedString(value: unknown): string | undefined {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
}

function toOptionalNumber(value: unknown): number | undefined {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}
	return undefined;
}

function toEpochMs(value: unknown, fallback: number): number {
	if (typeof value === "number" && Number.isFinite(value)) {
		return Math.floor(value);
	}

	if (typeof value === "string" && value.trim().length > 0) {
		const parsed = Date.parse(value);
		if (Number.isFinite(parsed)) {
			return parsed;
		}
	}

	return fallback;
}

function createFallbackId(prefix: string, index: number): string {
	const randomPart = Math.random().toString(36).slice(2, 10);
	return `${prefix}-${index}-${randomPart}`;
}

function resolveFetch(fetchImpl?: typeof fetch): typeof fetch {
	const resolved = fetchImpl ?? globalThis.fetch;
	if (typeof resolved !== "function") {
		throw new Error("Fetch implementation is not available.");
	}
	return resolved;
}

function normalizeApiBaseUrl(baseUrl: string): string {
	const trimmed = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
	if (trimmed.endsWith("/api/v1")) {
		return trimmed;
	}
	return `${trimmed}/api/v1`;
}

function normalizeOrganisationId(organisationId: string): string {
	const trimmed = organisationId.trim();
	if (trimmed.length === 0) {
		throw new Error("Organisation ID is required.");
	}
	return trimmed;
}

function buildCustomInsightsEndpoint(
	baseUrl: string,
	organisationId: string,
	insightId?: string,
): string {
	const normalizedBaseUrl = normalizeApiBaseUrl(baseUrl);
	const normalizedOrganisationId = normalizeOrganisationId(organisationId);
	const basePath = `${normalizedBaseUrl}/custom_insights/${encodeURIComponent(normalizedOrganisationId)}/insights`;
	if (!insightId) {
		return basePath;
	}
	return `${basePath}/${encodeURIComponent(insightId)}`;
}

function buildCustomDashboardsEndpoint(
	baseUrl: string,
	organisationId: string,
	dashboardId?: string,
): string {
	const normalizedBaseUrl = normalizeApiBaseUrl(baseUrl);
	const normalizedOrganisationId = normalizeOrganisationId(organisationId);
	const basePath = `${normalizedBaseUrl}/custom_insights/${encodeURIComponent(normalizedOrganisationId)}/dashboards`;
	if (!dashboardId) {
		return basePath;
	}
	return `${basePath}/${encodeURIComponent(dashboardId)}`;
}

function extractErrorMessage(body: unknown, fallback: string): string {
	if (isRecord(body)) {
		if (typeof body.message === "string" && body.message.trim().length > 0) {
			return body.message;
		}
		if (typeof body.detail === "string" && body.detail.trim().length > 0) {
			return body.detail;
		}
	}

	return fallback;
}

async function requestJson<T>(params: {
	options: CustomInsightsApiRequestOptions;
	url: string;
	method: "GET" | "POST" | "PATCH" | "DELETE";
	body?: unknown;
}): Promise<T> {
	const fetchFn = resolveFetch(params.options.fetchImpl);
	const headers = new Headers();
	headers.set("accept", "application/json");
	if (params.body !== undefined) {
		headers.set("content-type", "application/json");
	}
	if (
		typeof params.options.token === "string" &&
		params.options.token.trim().length > 0
	) {
		headers.set("tracify-token", params.options.token);
	}

	const response = await fetchFn(params.url, {
		method: params.method,
		headers,
		body: params.body === undefined ? undefined : JSON.stringify(params.body),
		signal: params.options.signal,
	});

	if (response.status === 204) {
		return undefined as T;
	}

	const rawText = await response.text();
	let parsedBody: unknown = undefined;

	if (rawText.length > 0) {
		try {
			parsedBody = JSON.parse(rawText);
		} catch {
			parsedBody = rawText;
		}
	}

	if (!response.ok) {
		throw new CustomInsightsApiRequestError({
			message: extractErrorMessage(
				parsedBody,
				`Custom insights API request failed with status ${response.status}.`,
			),
			status: response.status,
			body: parsedBody,
		});
	}

	return parsedBody as T;
}

function normalizeSortMetric(
	value: unknown,
	fallbackMetric: string = DEFAULT_METRIC,
): string {
	if (typeof value === "string" && value.trim().length > 0) {
		return value;
	}

	if (Array.isArray(value)) {
		for (const item of value) {
			if (typeof item === "string" && item.trim().length > 0) {
				return item;
			}
		}
	}

	return fallbackMetric;
}

function normalizeFilterValue(
	value: unknown,
): string | number | (string | number)[] {
	if (typeof value === "string" || typeof value === "number") {
		return value;
	}

	if (Array.isArray(value)) {
		return value
			.filter(
				(item): item is string | number =>
					typeof item === "string" || typeof item === "number",
			)
			.map((item) => (typeof item === "string" ? item.trim() : item))
			.filter((item) => (typeof item === "string" ? item.length > 0 : true));
	}

	return "";
}

export function extractCustomInsightApiItem(
	body: unknown,
): CustomInsightsApiInsight {
	if (
		isRecord(body) &&
		typeof body.id === "string" &&
		Array.isArray(body.series)
	) {
		return body as CustomInsightsApiInsight;
	}

	if (isRecord(body) && isRecord(body.item)) {
		const item = body.item;
		if (typeof item.id === "string" && Array.isArray(item.series)) {
			return item as CustomInsightsApiInsight;
		}
	}

	throw new Error("Invalid custom insight response payload.");
}

export function extractCustomDashboardApiItem(
	body: unknown,
): CustomInsightsApiDashboard {
	if (
		isRecord(body) &&
		typeof body.id === "string" &&
		typeof body.title === "string" &&
		Array.isArray(body.tiles)
	) {
		return body as CustomInsightsApiDashboard;
	}

	if (isRecord(body) && isRecord(body.item)) {
		const item = body.item;
		if (
			typeof item.id === "string" &&
			typeof item.title === "string" &&
			Array.isArray(item.tiles)
		) {
			return item as CustomInsightsApiDashboard;
		}
	}

	throw new Error("Invalid custom dashboard response payload.");
}

export function mapCustomInsightApiItemToInsightConfig(
	item: CustomInsightsApiInsight,
): InsightConfigLike {
	const now = Date.now();
	const createdAt = toEpochMs(item.created_at, now);
	const updatedAt = toEpochMs(item.updated_at, createdAt);

	const sortedSeries = [...(item.series ?? [])].sort((a, b) => {
		const aOrder = toOptionalNumber(a.series_order) ?? 0;
		const bOrder = toOptionalNumber(b.series_order) ?? 0;
		return aOrder - bOrder;
	});

	const series = sortedSeries.map((rawSeries, index) => {
		const label =
			toTrimmedString(rawSeries.label) ??
			String.fromCharCode("A".charCodeAt(0) + index);

		const filters = Array.isArray(rawSeries.filters)
			? rawSeries.filters.map((filter, filterIndex) => ({
					id:
						toTrimmedString(filter.id) ??
						createFallbackId(`series-filter-${index}`, filterIndex),
					name: String(filter.name ?? ""),
					operator: String(filter.operator ?? "eq"),
					value: normalizeFilterValue(filter.value),
				}))
			: [];

		return {
			id:
				toTrimmedString(rawSeries.id) ??
				createFallbackId(`series-${item.id}`, index),
			label,
			metric: String(rawSeries.metric ?? DEFAULT_METRIC),
			filters,
			color: toTrimmedString(rawSeries.color) ?? "",
		};
	});

	const filters = Array.isArray(item.filters)
		? item.filters.map((filter, index) => ({
				id:
					toTrimmedString(filter.id) ??
					createFallbackId(`filter-${item.id}`, index),
				name: String(filter.name ?? ""),
				operator: String(filter.operator ?? "eq"),
				value: normalizeFilterValue(filter.value),
			}))
		: [];

	const sortedBreakdowns = [...(item.breakdowns ?? [])].sort((a, b) => {
		const aOrder = toOptionalNumber(a.breakdown_order) ?? 0;
		const bOrder = toOptionalNumber(b.breakdown_order) ?? 0;
		return aOrder - bOrder;
	});

	const fallbackMetric = series[0]?.metric ?? DEFAULT_METRIC;
	const breakdown = sortedBreakdowns.map((rawBreakdown) => ({
		dimension: String(rawBreakdown.dimension ?? "campaign"),
		limit:
			Math.max(1, Math.floor(toOptionalNumber(rawBreakdown.limit) ?? 25)) ?? 25,
		sortBy: normalizeSortMetric(rawBreakdown.sort_by, fallbackMetric),
		sortOrder: rawBreakdown.sort_order === "asc" ? "asc" : "desc",
	}));

	const rawDisplayOptions = isRecord(item.display_options)
		? item.display_options
		: {};
	const rawYAxisUnit = isRecord(rawDisplayOptions.y_axis_unit)
		? rawDisplayOptions.y_axis_unit
		: undefined;

	return {
		id: item.id,
		title: toTrimmedString(item.title) ?? "Untitled Insight",
		description:
			typeof item.description === "string" ? item.description : undefined,
		type: toTrimmedString(item.type) ?? DEFAULT_INSIGHT_TYPE,
		attributionPreset: toTrimmedString(item.attribution_preset_id) ?? "",
		series,
		filters,
		filterOperator: item.filter_operator === "or" ? "or" : "and",
		breakdown,
		timeRange: {
			preset:
				toTrimmedString(item.time_range?.preset) ?? DEFAULT_TIME_RANGE_PRESET,
			customDays: toOptionalNumber(item.time_range?.custom_days),
		},
		useOwnTimeRange:
			typeof item.use_own_time_range === "boolean"
				? item.use_own_time_range
				: undefined,
		granularity:
			item.granularity === "none" ||
			item.granularity === "daily" ||
			item.granularity === "weekly" ||
			item.granularity === "monthly"
				? item.granularity
				: DEFAULT_GRANULARITY,
		comparisonPeriod:
			toTrimmedString(item.comparison_period) ?? DEFAULT_COMPARISON_PERIOD,
		displayOptions: {
			showLegend:
				typeof rawDisplayOptions.show_legend === "boolean"
					? rawDisplayOptions.show_legend
					: false,
			showMultipleYAxes:
				typeof rawDisplayOptions.show_multiple_y_axes === "boolean"
					? rawDisplayOptions.show_multiple_y_axes
					: false,
			showSummaryStats:
				typeof rawDisplayOptions.show_summary_stats === "boolean"
					? rawDisplayOptions.show_summary_stats
					: false,
			yAxisUnit: rawYAxisUnit
				? {
						type: toTrimmedString(rawYAxisUnit.type) ?? "none",
						customValue: toTrimmedString(rawYAxisUnit.custom_value),
					}
				: undefined,
		},
		schemaVersion: 1,
		createdAt,
		updatedAt,
	};
}

export function mapInsightConfigToCustomInsightUpsertInput(
	input: Partial<InsightConfigLike>,
): CustomInsightsApiUpsertInput {
	const payload: CustomInsightsApiUpsertInput = {};

	if (typeof input.type === "string") {
		payload.type = input.type;
	}
	if (typeof input.attributionPreset === "string") {
		payload.attribution_preset_id = input.attributionPreset;
	}
	if (Array.isArray(input.series)) {
		payload.series = input.series.map((series, index) => ({
			metric: String(series.metric ?? DEFAULT_METRIC),
			filters: Array.isArray(series.filters)
				? series.filters.map((filter) => ({
						name: String(filter.name ?? ""),
						operator: String(filter.operator ?? "eq"),
						value: normalizeFilterValue(filter.value),
					}))
				: undefined,
			label: typeof series.label === "string" ? series.label : undefined,
			color: typeof series.color === "string" ? series.color : undefined,
			series_order: index,
		}));
	}
	if (Array.isArray(input.filters)) {
		payload.filters = input.filters.map((filter) => ({
			name: String(filter.name ?? ""),
			operator: String(filter.operator ?? "eq"),
			value: normalizeFilterValue(filter.value),
		}));
	}
	if (input.filterOperator === "and" || input.filterOperator === "or") {
		payload.filter_operator = input.filterOperator;
	}
	if (Array.isArray(input.breakdown)) {
		payload.breakdowns = input.breakdown.map((breakdown, index) => ({
			dimension: String(breakdown.dimension ?? "campaign"),
			sort_order: breakdown.sortOrder === "asc" ? "asc" : "desc",
			sort_by:
				typeof breakdown.sortBy === "string" && breakdown.sortBy.length > 0
					? [breakdown.sortBy]
					: [DEFAULT_METRIC],
			limit:
				typeof breakdown.limit === "number" && Number.isFinite(breakdown.limit)
					? Math.max(1, Math.floor(breakdown.limit))
					: 25,
			breakdown_order: index,
		}));
	}
	if (isRecord(input.timeRange)) {
		payload.time_range = {
			preset:
				typeof input.timeRange.preset === "string"
					? input.timeRange.preset
					: undefined,
			custom_days:
				typeof input.timeRange.customDays === "number" &&
				Number.isFinite(input.timeRange.customDays)
					? Math.max(1, Math.floor(input.timeRange.customDays))
					: undefined,
		};
	}
	if (
		input.granularity === "none" ||
		input.granularity === "daily" ||
		input.granularity === "weekly" ||
		input.granularity === "monthly"
	) {
		payload.granularity = input.granularity;
	}
	if (typeof input.comparisonPeriod === "string") {
		payload.comparison_period = input.comparisonPeriod;
	}

	if (isRecord(input.displayOptions)) {
		const displayOptions = input.displayOptions;
		const yAxisUnit = isRecord(displayOptions.yAxisUnit)
			? displayOptions.yAxisUnit
			: undefined;

		payload.display_options = {
			show_legend:
				typeof displayOptions.showLegend === "boolean"
					? displayOptions.showLegend
					: undefined,
			show_multiple_y_axes:
				typeof displayOptions.showMultipleYAxes === "boolean"
					? displayOptions.showMultipleYAxes
					: undefined,
			show_summary_stats:
				typeof displayOptions.showSummaryStats === "boolean"
					? displayOptions.showSummaryStats
					: undefined,
			y_axis_unit: yAxisUnit
				? {
						type: toTrimmedString(yAxisUnit.type),
						custom_value: toTrimmedString(yAxisUnit.customValue),
					}
				: undefined,
		};
	}

	if (typeof input.title === "string") {
		payload.title = input.title;
	}
	if (typeof input.description === "string") {
		payload.description = input.description;
	}
	if (typeof input.useOwnTimeRange === "boolean") {
		payload.use_own_time_range = input.useOwnTimeRange;
	}

	return payload;
}

export async function listCustomInsights(
	options: CustomInsightsApiRequestOptions & {
		query?: string;
		limit?: number;
		offset?: number;
	},
): Promise<CustomInsightsApiListResponse> {
	const url = new URL(
		buildCustomInsightsEndpoint(options.baseUrl, options.organisationId),
	);
	if (typeof options.query === "string" && options.query.trim().length > 0) {
		url.searchParams.set("query", options.query);
	}
	if (typeof options.limit === "number" && Number.isFinite(options.limit)) {
		url.searchParams.set(
			"limit",
			String(Math.max(1, Math.floor(options.limit))),
		);
	}
	if (typeof options.offset === "number" && Number.isFinite(options.offset)) {
		url.searchParams.set(
			"offset",
			String(Math.max(0, Math.floor(options.offset))),
		);
	}

	const body = await requestJson<unknown>({
		options,
		url: url.toString(),
		method: "GET",
	});

	if (!isRecord(body) || !Array.isArray(body.items)) {
		throw new Error("Invalid custom insights list response payload.");
	}

	return {
		items: body.items as CustomInsightsApiInsight[],
		count:
			typeof body.count === "number" && Number.isFinite(body.count)
				? Math.max(0, Math.floor(body.count))
				: body.items.length,
	};
}

export async function getCustomInsight(
	options: CustomInsightsApiRequestOptions,
	insightId: string,
): Promise<CustomInsightsApiInsight> {
	const body = await requestJson<unknown>({
		options,
		url: buildCustomInsightsEndpoint(
			options.baseUrl,
			options.organisationId,
			insightId,
		),
		method: "GET",
	});

	return extractCustomInsightApiItem(body);
}

export async function createCustomInsight(
	options: CustomInsightsApiRequestOptions,
	input: CustomInsightsApiUpsertInput,
): Promise<CustomInsightsApiInsight> {
	const body = await requestJson<unknown>({
		options,
		url: buildCustomInsightsEndpoint(options.baseUrl, options.organisationId),
		method: "POST",
		body: input,
	});

	return extractCustomInsightApiItem(body);
}

export async function updateCustomInsight(
	options: CustomInsightsApiRequestOptions,
	insightId: string,
	input: CustomInsightsApiUpsertInput,
): Promise<CustomInsightsApiInsight> {
	const body = await requestJson<unknown>({
		options,
		url: buildCustomInsightsEndpoint(
			options.baseUrl,
			options.organisationId,
			insightId,
		),
		method: "PATCH",
		body: input,
	});

	return extractCustomInsightApiItem(body);
}

export async function deleteCustomInsight(
	options: CustomInsightsApiRequestOptions,
	insightId: string,
): Promise<void> {
	await requestJson<void>({
		options,
		url: buildCustomInsightsEndpoint(
			options.baseUrl,
			options.organisationId,
			insightId,
		),
		method: "DELETE",
	});
}

export async function listCustomDashboards(
	options: CustomInsightsApiRequestOptions & {
		query?: string;
		limit?: number;
		offset?: number;
	},
): Promise<CustomInsightsApiDashboardListResponse> {
	const url = new URL(
		buildCustomDashboardsEndpoint(options.baseUrl, options.organisationId),
	);
	if (typeof options.query === "string" && options.query.trim().length > 0) {
		url.searchParams.set("query", options.query);
	}
	if (typeof options.limit === "number" && Number.isFinite(options.limit)) {
		url.searchParams.set(
			"limit",
			String(Math.max(1, Math.floor(options.limit))),
		);
	}
	if (typeof options.offset === "number" && Number.isFinite(options.offset)) {
		url.searchParams.set(
			"offset",
			String(Math.max(0, Math.floor(options.offset))),
		);
	}

	const body = await requestJson<unknown>({
		options,
		url: url.toString(),
		method: "GET",
	});

	if (!isRecord(body) || !Array.isArray(body.items)) {
		throw new Error("Invalid custom dashboards list response payload.");
	}

	return {
		items: body.items.map((item) => extractCustomDashboardApiItem(item)),
		count:
			typeof body.count === "number" && Number.isFinite(body.count)
				? Math.max(0, Math.floor(body.count))
				: body.items.length,
	};
}

export async function getCustomDashboard(
	options: CustomInsightsApiRequestOptions,
	dashboardId: string,
): Promise<CustomInsightsApiDashboard> {
	const body = await requestJson<unknown>({
		options,
		url: buildCustomDashboardsEndpoint(
			options.baseUrl,
			options.organisationId,
			dashboardId,
		),
		method: "GET",
	});

	return extractCustomDashboardApiItem(body);
}

export async function createCustomDashboard(
	options: CustomInsightsApiRequestOptions,
	input: CustomInsightsApiDashboardUpsertInput,
): Promise<CustomInsightsApiDashboard> {
	const body = await requestJson<unknown>({
		options,
		url: buildCustomDashboardsEndpoint(options.baseUrl, options.organisationId),
		method: "POST",
		body: input,
	});

	return extractCustomDashboardApiItem(body);
}

export async function updateCustomDashboard(
	options: CustomInsightsApiRequestOptions,
	dashboardId: string,
	input: CustomInsightsApiDashboardUpsertInput,
): Promise<CustomInsightsApiDashboard> {
	const body = await requestJson<unknown>({
		options,
		url: buildCustomDashboardsEndpoint(
			options.baseUrl,
			options.organisationId,
			dashboardId,
		),
		method: "PATCH",
		body: input,
	});

	return extractCustomDashboardApiItem(body);
}

export async function deleteCustomDashboard(
	options: CustomInsightsApiRequestOptions,
	dashboardId: string,
): Promise<void> {
	await requestJson<void>({
		options,
		url: buildCustomDashboardsEndpoint(
			options.baseUrl,
			options.organisationId,
			dashboardId,
		),
		method: "DELETE",
	});
}
